import { MemberRole } from 'src/features/member/types'
import type { components } from 'src/lib/api-client/schema.gen'

import { IcfDocumentSet } from '../icf-document-set/entity'

export type IcfDocument = {
  uid: string
  numberingId: number
  type: IcfDocumentType
}

export type IcfDocumentForList = IcfDocument & {
  icfDocumentRevisionUid: string
  name: string
  version: string
  status: components['schemas']['output.IcfRevStatus']
  isRequiredPatientSign: boolean
  publishedAt: string
  isRevising: boolean
  isLinkedICFDocumentSet: boolean
  isRequiredReAgreementConfirmation: boolean
}

export type IcfDocumentDetail = IcfDocument & {
  canDelete: boolean
  latestRevisionUid: string
  revisions: IcfDocumentRevisionForMenu[]
  sets: IcfDocumentSetForIcfDocumentDetail[]
}

type IcfDocumentRevisionForMenu = {
  uid: string
  version: string
  approvedAt: string
}

export type IcfDocumentSetForIcfDocumentDetail = Pick<
  IcfDocumentSet,
  'uid' | 'numberingId' | 'name' | 'enabled'
>

export type CreateIcfDocumentItem = {
  uid: string
  type: IcfDocumentType | undefined
  tempFileUid: string
  dsTemplateId: string
  revision: IcfDocumentRevisionForCreate
}

type IcfDocumentRevisionForCreate = Pick<
  IcfDocumentRevision,
  'uid' | 'version' | 'name' | 'fileName' | 'isRequiredPatientSign'
>

export type IcfDocumentRevision = {
  uid: string
  icfDocumentUid: string
  version: string
  name: string
  fileName: string
  type: IcfDocumentType
  latestStatus: components['schemas']['output.IcfRevStatus']
  approvedAt: string
  isRequiredPatientSign: boolean
  signFields: SignField[]
  checkRoles: IcfDocumentCheckRole[]
  canCancel: boolean
  dsTemplateId: string
  dsTemplateModifiedAt?: string
  histories: IcfDocumentRevisionHistory[]
}

export type SignField = {
  signFieldType: 'Dr' | 'CRC' | 'Patient' | 'Representative'
  optional: boolean
}

export type IcfDocumentRevisionHistory = {
  uid: string
  action: components['schemas']['output.IcfRevAction']
  operatedAt: string
  operatedMemberName: string
  operatedMemberRole: MemberRole
  comment: string
}

export type NextIcfDocumentRevisionTemplate = {
  uid: string
  icfDocumentUid: string
  version: string
  name: string
  type: IcfDocumentType
  isRequiredPatientSign: boolean
}

export type CreateIcfDocumentRevisionItem = NextIcfDocumentRevisionTemplate & {
  fileName: string
  dsTemplateId?: string
  tempFileUid: string
}

export type IcfDocumentType =
  (typeof icfDocumentTypes)[keyof typeof icfDocumentTypes]

export const icfDocumentTypes = {
  Undefined: 0,
  AgreementForm: 1,
  CheckUnderstanding: 2,
  Description: 3,
  Video: 4,
} as const

export const icfDocumentTypeText: Record<IcfDocumentType, string> = {
  [icfDocumentTypes.Undefined]: '',
  [icfDocumentTypes.AgreementForm]: '同意書',
  [icfDocumentTypes.CheckUnderstanding]: '理解度確認',
  [icfDocumentTypes.Description]: '説明文書',
  [icfDocumentTypes.Video]: '動画',
}

export const isDocuSignType = (type: IcfDocumentType) =>
  type === icfDocumentTypes.AgreementForm ||
  type === icfDocumentTypes.CheckUnderstanding

export const icfRevActionText: Record<
  components['schemas']['output.IcfRevAction'],
  string
> = {
  Create: '新規作成',
  Save: '一時保存',
  Publish: '公開',
  CreateNewRevision: '改版開始',
  CancelNewRevision: '改版取り消し',
  Disable: '無効化',
  Enable: '有効化',

  // 承認フロー撤廃以前のアクション
  RequestApproval: '承認依頼',
  Approve: '承認',
  Reject: '承認却下',
  Withdraw: '承認依頼取り下げ',
}

export type IcfDocumentSignerRole =
  (typeof icfDocumentSignerRoles)[keyof typeof icfDocumentSignerRoles]

export type IcfDocumentCheckRole =
  (typeof _icfDocumentCheckRoles)[keyof typeof _icfDocumentCheckRoles]

export const icfDocumentSignerRoles = {
  Dr: 1,
  CRC: 2,
  Patient: 3,
  Representative: 4,
} as const

const _icfDocumentCheckRoles = {
  Patient: 3,
  Representative: 4,
} as const

export const icfDocumentSignFieldTypes = [
  'Dr',
  'CRC',
  'Patient',
  'Representative',
]

export const icfDocumentSignFieldTypeText: Record<string, string> = {
  Dr: 'Dr',
  CRC: 'CRC',
  Patient: '患者',
  Representative: '代諾者',
}

export const icfDocumentSignFieldTypeToRole: Record<
  string,
  IcfDocumentSignerRole
> = {
  Dr: icfDocumentSignerRoles.Dr,
  CRC: icfDocumentSignerRoles.CRC,
  Patient: icfDocumentSignerRoles.Patient,
  Representative: icfDocumentSignerRoles.Representative,
}
